import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    primary: {
      main: '#06b6d4',
      light: '#22d3ee',
      dark: '#0891b2',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#8b5cf6',
      light: '#a78bfa',
      dark: '#7c3aed',
      contrastText: '#ffffff',
    },
    success: {
      main: '#10b981',
      light: '#34d399',
      dark: '#059669',
    },
    warning: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
    },
    error: {
      main: '#f43f5e',
      light: '#fb7185',
      dark: '#e11d48',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
    text: {
      primary: '#0f172a',
      secondary: '#64748b',
    },
    divider: 'rgba(15, 23, 42, 0.08)',
  },
  typography: {
    fontFamily: '"Nunito", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h1: {
      fontWeight: 800,
      fontSize: '2.5rem',
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
      color: '#0f172a',
    },
    h2: {
      fontWeight: 700,
      fontSize: '2rem',
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
      color: '#0f172a',
    },
    h3: {
      fontWeight: 700,
      fontSize: '1.5rem',
      lineHeight: 1.4,
      color: '#0f172a',
    },
    h4: {
      fontWeight: 700,
      fontSize: '1.25rem',
      lineHeight: 1.4,
      color: '#0f172a',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.1rem',
      lineHeight: 1.5,
      color: '#0f172a',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.5,
      color: '#0f172a',
    },
    body1: {
      fontWeight: 400,
      fontSize: '1rem',
      lineHeight: 1.6,
      color: '#334155',
    },
    body2: {
      fontWeight: 400,
      fontSize: '0.875rem',
      lineHeight: 1.6,
      color: '#64748b',
    },
    button: {
      fontWeight: 600,
      textTransform: 'none' as const,
      letterSpacing: '0.01em',
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0 1px 3px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.04)',
    '0 2px 6px rgba(0,0,0,0.08), 0 6px 16px rgba(0,0,0,0.05)',
    '0 4px 12px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.06)',
    '0 6px 16px rgba(0,0,0,0.1), 0 12px 32px rgba(0,0,0,0.08)',
    '0 8px 20px rgba(0,0,0,0.1), 0 16px 40px rgba(0,0,0,0.08)',
    '0 10px 24px rgba(0,0,0,0.12), 0 20px 48px rgba(0,0,0,0.1)',
    '0 12px 28px rgba(0,0,0,0.12), 0 24px 52px rgba(0,0,0,0.1)',
    '0 14px 32px rgba(0,0,0,0.14), 0 28px 56px rgba(0,0,0,0.12)',
    '0 16px 36px rgba(0,0,0,0.14), 0 32px 60px rgba(0,0,0,0.12)',
    '0 18px 40px rgba(0,0,0,0.16), 0 36px 64px rgba(0,0,0,0.14)',
    '0 20px 44px rgba(0,0,0,0.16), 0 40px 68px rgba(0,0,0,0.14)',
    '0 22px 48px rgba(0,0,0,0.18), 0 44px 72px rgba(0,0,0,0.16)',
    '0 24px 52px rgba(0,0,0,0.18), 0 48px 76px rgba(0,0,0,0.16)',
    '0 26px 56px rgba(0,0,0,0.2), 0 52px 80px rgba(0,0,0,0.18)',
    '0 28px 60px rgba(0,0,0,0.2), 0 56px 84px rgba(0,0,0,0.18)',
    '0 30px 64px rgba(0,0,0,0.22), 0 60px 88px rgba(0,0,0,0.2)',
    '0 32px 68px rgba(0,0,0,0.22), 0 64px 92px rgba(0,0,0,0.2)',
    '0 34px 72px rgba(0,0,0,0.24), 0 68px 96px rgba(0,0,0,0.22)',
    '0 36px 76px rgba(0,0,0,0.24), 0 72px 100px rgba(0,0,0,0.22)',
    '0 38px 80px rgba(0,0,0,0.26), 0 76px 104px rgba(0,0,0,0.24)',
    '0 40px 84px rgba(0,0,0,0.26), 0 80px 108px rgba(0,0,0,0.24)',
    '0 42px 88px rgba(0,0,0,0.28), 0 84px 112px rgba(0,0,0,0.26)',
    '0 44px 92px rgba(0,0,0,0.28), 0 88px 116px rgba(0,0,0,0.26)',
    '0 46px 96px rgba(0,0,0,0.3), 0 92px 120px rgba(0,0,0,0.28)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: '10px 24px',
          fontSize: '0.95rem',
          fontWeight: 600,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-1px)',
          },
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #06b6d4, #8b5cf6)',
          boxShadow: '0 4px 14px rgba(6, 182, 212, 0.35)',
          '&:hover': {
            background: 'linear-gradient(135deg, #0891b2, #7c3aed)',
            boxShadow: '0 6px 20px rgba(6, 182, 212, 0.45)',
          },
        },
        outlined: {
          borderWidth: '2px',
          '&:hover': {
            borderWidth: '2px',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: '1px solid #f1f5f9',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.04)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.1), 0 16px 40px rgba(0,0,0,0.06)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
        elevation1: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.04)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            transition: 'all 0.3s ease',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#06b6d4',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#06b6d4',
              borderWidth: '2px',
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          fontWeight: 500,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
          boxShadow: '0 24px 48px rgba(0,0,0,0.16)',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 8,
          fontSize: '0.8rem',
          fontWeight: 500,
          padding: '8px 14px',
          backgroundColor: '#0f172a',
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontWeight: 600,
        },
      },
    },
  },
});

export default theme;
